import React, { Component, ErrorInfo, ReactNode } from "react";
import { WifiOff, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  component?: React.ComponentType<{ error: Error }>;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

function isDynamicImportError(error: Error): boolean {
  const message = error?.message?.toLowerCase() || "";
  return (
    message.includes("dynamically imported module") ||
    message.includes("failed to fetch") ||
    message.includes("loading chunk") ||
    message.includes("loading css chunk") ||
    error?.name === "ChunkLoadError"
  );
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error: error,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  public render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.component) {
        const ErrorComponent = this.props.component;
        return <ErrorComponent error={this.state.error} />;
      }

      const isOffline = !navigator.onLine;
      const isChunkError = isDynamicImportError(this.state.error);

      if (isOffline || isChunkError) {
        return (
          <div className="p-4 text-center">
            <WifiOff className="w-8 h-8 mx-auto text-amber-500 mb-2" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {isOffline ? "You're offline" : "Connection issue"}
            </h2>
            <p className="text-gray-600 mb-4">
              {isOffline
                ? "Please check your internet connection."
                : "We had trouble loading this content."}
            </p>
            <Button
              onClick={() => window.location.reload()}
              size="sm"
              className="inline-flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </Button>
          </div>
        );
      }

      return (
        <div className="p-4 text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">
            Something went wrong
          </h2>
          <p className="text-gray-600">
            {this.state.error.message || "Please try refreshing the page"}
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
